import {
  CLICKS_COUNT_FOR_INTENSIVE_ANIMATION,
  CLICKS_COUNT_FOR_SHASHLYK_ANIMATION,
  KOTLETKA_INACTIVE_TIME,
  RESET_TO_COMMON_CLICK_ANIMATION_TIME,
} from '../../../ClickerGame.constants';
import {ClickerGCSEngine} from '../../../ClickerGame.types';
import {DI_TOKENS} from '@/di/di.tokens';
import debounce from 'lodash.debounce';

import {injected} from 'brandi';

import {BaseComponent} from '@specials/gcs-engine';

import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {GlobalWindow} from '@/types';
import {CallbackCollector} from '@/utils/CallbackCollector';
import {DateUtils} from '@/utils/DateUtils';

import {ClickerGameEventNames, ClickerGameEvents} from '../../../ClickerGameEvents';
import {KotletkaState, SpaceshipAnimationComponent} from './SpaceshipAnimationComponent';

export class SpaceshipAnimationManagerComponent extends BaseComponent {
  private animationComponent?: SpaceshipAnimationComponent;
  // общее количество кликов
  private clickCounter: number;
  // безостановочное количество кликов
  private clickComboCounter: number;

  constructor(
    private readonly gcs: ClickerGCSEngine,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly callbacksCollector: CallbackCollector,
    private readonly clickerEvents: ClickerGameEvents,
    private readonly globalWindow: GlobalWindow,
  ) {
    super();
  }

  public onAddToWorld(): void {
    this.clickCounter = 0;
    this.clickComboCounter = 0;
    this.animationComponent = this.gameObject.getComponent(SpaceshipAnimationComponent);
    this.callbacksCollector.add(this.clickerStateStore.subscribe(this.handleClickerStateChanged));
    this.callbacksCollector.add(this.clickerEvents.subscribeOnEvent(ClickerGameEventNames.Tap, this.handleTap));
  }

  public start(): void {
    const animationComponent = this.animationComponent;

    if (!animationComponent) return;

    animationComponent.kotletkaCurrentState = animationComponent.getKotletkaStateByChargeLevel();

    // если у котлеты заряжена батарея, то ночью показываем сразу
    if (animationComponent.kotletkaCurrentState !== KotletkaState.Sad) {
      if (DateUtils.isLocalNight()) {
        animationComponent.kotletkaCurrentState = KotletkaState.Sleepy;
      } else {
        this.gcs.time.setInterval(animationComponent.forcePlayTransitionToSleepy, KOTLETKA_INACTIVE_TIME);
      }
    }

    animationComponent.playCurrentAnimation();

    this.globalWindow.addEventListener('pointerdown', this.handleWindowClick);
  }

  public destroy(): void {
    this.callbacksCollector.execute();
    this.globalWindow.removeEventListener('pointerdown', this.handleWindowClick);
  }

  public playSoundClickByState(): void {
    this.animationComponent?.playSoundClickByState();
  }

  public playClickAnimation(): void {
    const animationComponent = this.animationComponent;

    if (!animationComponent) return;

    const state = animationComponent.kotletkaCurrentState;

    if (animationComponent.reactionAnimation !== null || animationComponent.isSleepyState(state)) return;

    if (animationComponent.isHappyState(state)) {
      if (this.clickComboCounter >= CLICKS_COUNT_FOR_INTENSIVE_ANIMATION) {
        animationComponent.playIntensiveClickAnimation();
      } else {
        animationComponent.playHappyClickAnimation();
      }
    }

    if (animationComponent.isSadState(state)) {
      animationComponent.playSadClickAnimation();
    }
  }

  private handleWindowClick = (): void => {
    const animationComponent = this.animationComponent;

    if (!animationComponent) return;

    const state = animationComponent.kotletkaCurrentState;

    if (animationComponent.isSleepyState(state)) {
      animationComponent.kotletkaCurrentState = KotletkaState.TransitionSleepyToHappy;
      animationComponent.playCurrentAnimation();
    } else {
      this.gcs.time.clearInterval(animationComponent.forcePlayTransitionToSleepy);
      this.gcs.time.setInterval(animationComponent.forcePlayTransitionToSleepy, KOTLETKA_INACTIVE_TIME);
    }
  };

  private handleClickerStateChanged = (): void => {
    const animationComponent = this.animationComponent;

    if (!animationComponent) return;

    if (animationComponent.isTransitionState(animationComponent.kotletkaCurrentState)) return;

    const newState = animationComponent.getKotletkaStateByChargeLevel();
    const currentState = animationComponent.kotletkaCurrentState;

    const isStateChanged =
      (animationComponent.isSadState(newState) && animationComponent.isHappyState(currentState)) ||
      (animationComponent.isHappyState(newState) && animationComponent.isSadState(currentState));

    if (isStateChanged) {
      animationComponent.nextState();
    }
  };

  private handleTap = (): void => {
    this.clickCounter++;
    this.clickComboCounter++;

    if (this.clickCounter % CLICKS_COUNT_FOR_SHASHLYK_ANIMATION === 0) {
      this.animationComponent?.playShashlyk();
    }

    this.debouncedResetClickComboCounter();
  };

  public debouncedResetClickComboCounter = debounce(() => {
    this.clickComboCounter = 0;
  }, RESET_TO_COMMON_CLICK_ANIMATION_TIME);
}

injected(
  SpaceshipAnimationManagerComponent,
  DI_TOKENS.gcsEngine,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.callbackCollector,
  DI_TOKENS.clickerGameEvents,
  DI_TOKENS.globalWindow,
);
